import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useWorkflowApi from '../../api/workflows/useWorkflowApi';
import {
  submitFormData,
  extractAndSortSubforms,
} from '../../utils/formSubmissionUtils';

/**
 * useMyRequestDetails Hook
 *
 * Feature hook for viewing and editing user's own requests.
 * Handles request fetching, progress state, and form submission.
 *
 * @param {string} requestId - Request ID
 * @returns {Object} - { request, requestProgress, loading, error, handleSaveForm, workflowVersionName }
 */
const useMyRequestDetails = (requestId) => {
  const [request, setRequest] = useState(null);
  const [requestProgress, setRequestProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { getEndUserRequest } = useWorkflowApi();

  useEffect(() => {
    const fetchRequest = async () => {
      if (!requestId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getEndUserRequest(requestId);
        setRequest(data);

        // Find the ACTIVE progress record (should be only one)
        const activeProgress = data.requests_progresses?.find(
          (rp) => rp.active
        );
        // Fallback to most recent if no active found
        const currentProgress =
          activeProgress || data.requests_progresses?.slice().reverse()[0];
        setRequestProgress(currentProgress);
      } catch (err) {
        console.error('Error fetching request:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [requestId, getEndUserRequest]);

  const handleSaveForm = useCallback(
    async (allFormData) => {
      if (!requestProgress) return;

      await submitFormData({
        allFormData,
        payload: {
          // eslint-disable-next-line camelcase
          request_progress_id: requestProgress.id,
        },
        onSuccess: () => navigate(-1),
      });
    },
    [requestProgress, navigate]
  );

  const workflowVersionName = useMemo(
    () => request?.WorkflowVersion?.name || 'Workflow Version',
    [request]
  );

  const subforms = useMemo(() => {
    const stageChild = requestProgress?.StageRelation?.Child;
    return extractAndSortSubforms(stageChild, requestProgress?.subforms || []);
  }, [requestProgress]);

  return useMemo(
    () => ({
      request,
      requestProgress,
      subforms,
      loading,
      error,
      handleSaveForm,
      workflowVersionName,
    }),
    [
      request,
      requestProgress,
      subforms,
      loading,
      error,
      handleSaveForm,
      workflowVersionName,
    ]
  );
};

export default useMyRequestDetails;
